import React, { Suspense, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import ThreeController from '../three-controls/ThreeController';
import {
	createCSS2DRenderer,
	createCSS3DRenderer,
	initThreeJSScene,
	setupCamera,
	getRenderer,
	setupXR,
} from './setupThreeEditor';
import { threeEditorVREvents } from './threeEditorVREvents';
import { Background } from '@3jss/three-background';
import { isDesktop, isMobile, browserName } from 'react-device-detect';
import './main.scss';
import LoadingIcon from '@3jss/loadingIcon';
import TWEEN from '@tweenjs/tween.js';
import Renderer from './Renderer';
import { rotateCameraTowardsPoint } from '@3jss/Scene/sceneUtils';
import MouseListener from '@3jss/Scene/MouseListener';
import PropTypes from 'prop-types';
import KeyboardListener from '@3jss/Scene/KeyboardListener';
import VideoEquirectangular from '../three-background/VideoEquirectangular';
import {
	initXRSession,
	addCameraToDolly,
	removeCameraFromDolly,
} from '../VRUtils/xrSessionUtils';
import Stats from 'stats-gl';
import { CONTROL_MODES } from '@3jss/utils/constants';
import { HotspotsContainer } from '@3jss/Hotspot/HotspotsContainer';

const SceneModelObject = React.lazy(() =>
	import('@3jss/three-background/SceneModelObject'),
);
const AvatarLoader = React.lazy(() => import('@3jss/Avatars/AvatarLoader'));

const createOrGetCamera = (canvasRef, type, fov) => {
	const aspectRatio =
		canvasRef.current.offsetWidth / canvasRef.current.offsetHeight;
	const cameraKey = `${type}_camera`;
	window[cameraKey] =
		window[cameraKey] ||
		new THREE.PerspectiveCamera(fov, aspectRatio, 0.1, 1000);
	const audioListener = new THREE.AudioListener();
	window[cameraKey].add(audioListener);
	window[cameraKey].userData.audioListener = audioListener;
	return window[cameraKey];
};

const createOrGetControls = (
	cameraRef,
	renderer,
	orbitControlsConfig,
	type,
) => {
	const controllerKey = `${type}_controller`;

	if (window[controllerKey]) {
		return window[controllerKey];
	}

	window[controllerKey] = ThreeController.setupControls(
		cameraRef.current,
		renderer,
		orbitControlsConfig,
	);

	return window[controllerKey];
};

/**
 *
 * @description Scene component for threejs-scene
 * @module Scene
 * @returns {JSX.Element}
 *
 * @example
 *
 * <Scene
 *   sceneId={sceneId}
 *   bgConf={bgConf}
 *   debuggerConfig={debuggerConfig}
 *   type={type}
 *   enablePan={enablePan}
 *   orbitControlsConfig={orbitControlsConfig}
 *   cameraProperties={cameraProperties}
 * >
 *   {children}
 * </Scene>
 */
const Scene = (props) => {
	const {
		sceneId,
		bgConf,
		debuggerConfig = {},
		children,
		type,
		enablePan = false,
		orbitControlsConfig = {},
		cameraProperties = {},
		fov = 70,
		loadingIconSrc = null,
		primaryColor = null,
		secondaryColor = null,
		onBackgroundLoaded = () => {},
		onBackgroundReady = () => {},
		onSceneError = () => {},
		autoRotateConfig = {
			enabled: false,
			activateOnEachSceneLoad: false,
			cancelAutoRotateOnInteraction: true,
		},
		rotateCameraTowardsMovementDirection = false,
		enableZoomControls = false,
		transition,
		tooltipComponents = {},
		inSceneComponents = {},
		dampingFactors = { enable: false, factor: 0.1 },
		selectedAvatar,
		avatarConfiguration = {},
		allHotspotsLoaded = () => {},
		isXREnabled,
		xrSessionType,
		xrOptionalFeatures,
		xrEnvMapConfig,
		handleXRSessionEnd,
		sceneOrbitControlsConfig = {},
		controlMode = CONTROL_MODES.AVATAR_NAVIGATION,
		entranceVideoUrl,
		showEntranceVideo,
		onEntranceVideoEnd,
		proximityConfig,
	} = props;

	let RT3DControlsRef = useRef(null);
	const statsRef = useRef(
		new Stats({
			logsPerSecond: 20,
			samplesLog: 100,
			samplesGraph: 10,
			precision: 2,
			horizontal: true,
			minimal: false,
			mode: 0,
		}),
	);

	const [RT3DControlsReady, setRT3DControlsReady] = useState(false);

	const [threeReady, setThreeReady] = useState(false);
	const [showLoadingIcon, setShowLoadingIcon] = useState(true);
	const [renderObjects, setRenderObjects] = useState(false);
	const [UI, setUI] = useState();
	const [userInteracted, setUserInteracted] = useState(false);
	const [interactTimeoutId, setInteractTimeoutId] = useState(null);
	const [interactedWithNavMarker, setInteractedWithNavMarker] =
		useState(false);

	//Scene
	const sceneRef = useRef(new THREE.Scene());
	sceneRef.current.tooltipComponents = tooltipComponents;
	sceneRef.current.inSceneComponents = inSceneComponents;
	sceneRef.current.userData.sceneType = bgConf?.sceneType;
	const scene = sceneRef.current;

	// CSS 3D renderer scene
	const css3DSceneRef = useRef(new THREE.Scene());
	const css3DScene = css3DSceneRef.current;

	const clock = new THREE.Clock();

	const rendererRef = useRef(getRenderer(type));

	const css2DRendererRef = useRef(createCSS2DRenderer(type));
	const css2DRenderer = css2DRendererRef.current;

	const css3DRendererRef = useRef(createCSS3DRenderer(type));
	const css3DRenderer = css3DRendererRef.current;

	// useRef used to prevent Scene from losing variable references.
	const canvasRef = useRef();
	const cameraRef = useRef();
	const controlsRef = useRef();
	const activeHotspotRef = useRef();

	// Immersive Session helpers
	const vrControlsRef = useRef([]);
	const vrGripControlsRef = useRef([]);
	const vrHandsRef = useRef([]);
	const dollyCamRef = useRef();
	const [vrControlsLoaded, setVRControlsLoaded] = useState(false);
	const [isXRSessionReady, setIsXRSessionReady] = useState(false);

	sceneRef.current.setUI = setUI;

	sceneRef.current.addAnimationHook = (name, hook) => {
		if (!sceneRef?.current?.userData?.animation_loop_hooks) {
			sceneRef.current.userData.animation_loop_hooks = {};
		}
		sceneRef.current.userData.animation_loop_hooks[name] = hook;
	};

	sceneRef.current.removeAnimationHook = (name) => {
		if (
			sceneRef?.current?.userData?.animation_loop_hooks?.hasOwnProperty(
				name,
			)
		) {
			delete sceneRef.current.userData.animation_loop_hooks[name];
		}
	};

	/**
	 *
	 * @function onUserInteracted
	 * @description Listens for click events and sets userInteracted to true. Used to disable autPan on no interaction
	 */
	const onUserInteracted = () => {
		setUserInteracted(true);
		window.removeEventListener('click', onUserInteracted);
		if (interactTimeoutId) {
			clearTimeout(interactTimeoutId);
		}
	};

	/**
	 * @function onLookAt
	 *
	 * @description Used to rotate camera to a room object.
	 * RoomObject is passed as a detail in the event.
	 *
	 *
	 * @param event {CustomEvent}
	 * @param event.detail.roomObject {Object} - Room Object to look at
	 * @param event.detail.roomObject._id {Object} - Room Object ID
	 *
	 */
	const onLookAt = (event) => {
		const found = sceneRef.current.children.find((child) => {
			return child?.userData?._id?.$oid === event.detail.roomObject;
		});
		if (!found) {
			return;
		}
		if (event.detail?.click) {
			found?.onClick(true);
		} else if (found && found?.focus) {
			found.focus();
		}
	};

	/**
	 * @description useEffect that listens for renderObjects to be true.
	 * If renderObjects is true, it will set a timeout to show tooltips after 4 seconds if user
	 * has not interacted with the scene.
	 */
	useEffect(() => {
		if (renderObjects) {
			if (!userInteracted && !interactTimeoutId) {
				const itid = setTimeout(() => {
					if (!userInteracted) {
						setTooltipsVisibility(true);
					}
				}, 4000);
				setInteractTimeoutId(itid);
			}
		}
	}, [renderObjects]);

	/**
	 * @function animate
	 * @description Animation loop for scene
	 * @param controllerUpdate {Function} - Function to update controls
	 */
	const animate = (controllerUpdate = false) => {
		rendererRef.current?.setAnimationLoop(() => {
			// statsRef.current.begin();

			const delta = clock.getDelta();
			controlsRef.current.update(delta);

			rendererRef.current?.render(scene, cameraRef.current);
			css2DRenderer.render(scene, cameraRef.current);
			css3DRenderer.render(css3DScene, cameraRef.current);
			TWEEN.update();

			if (scene.userData.hasOwnProperty('animation_loop_hooks')) {
				Object.values(scene.userData.animation_loop_hooks).forEach(
					(hook) => hook(delta),
				);
			}

			statsRef.current.update();
		});
	};

	const setCurrentCamera = (camera) => {
		cameraRef.current = camera;
	};

	/**
	 *
	 * @function onMount
	 * @description Default useEffect.
	 *
	 *  - Sets up camera, controls, css2DRenderer, css3DRenderer
	 *  - Adds event listeners for click and lookAtEvent
	 *  - Sets threeReady state to true
	 *
	 */
	useEffect(() => {
		console.log('%c ===>INIT:1 - initThreeJSScene');
		initThreeJSScene({
			canvasRef,
			cameraRef,
			scene,
			css2DRendererRef,
			css3DRendererRef,
			type,
			enableZoomControls,
			isRT3D: bgConf?.sceneType === '3d_model_scene',
		});
		window.addEventListener('click', onUserInteracted);
		window.addEventListener('lookAtEvent', onLookAt);
		setThreeReady(true);

		return () => {
			console.log('%c >INIT:1 - unmount', 'color:red');

			canvasRef.current = null;
			clearRoom(); // Dispose the renderer and scene
			window.removeEventListener('click', onUserInteracted);
			window.removeEventListener('lookAtEvent', onLookAt);
		};
	}, []);

	const initControlsForVR = () => {
		const { vrControllers, gripControls, vrHands, handModels } =
			ThreeController.setupVRControls(
				rendererRef.current,
				dollyCamRef.current,
				sceneRef.current,
				xrEnvMapConfig,
			);
		vrControlsRef.current = vrControllers;
		vrGripControlsRef.current = gripControls;
		vrHandsRef.current = vrHands;
		setVRControlsLoaded(true);
	};

	/**
	 * @function initRoomForVR
	 * @description Sets up VR controls and hands
	 */
	const initRoomForVR = () => {
		initControlsForVR();
	};

	/**
	 * @function initDollyforVR
	 * @description Sets up dolly for VR camera rotation and movement
	 */
	const initDollyforVR = () => {
		if (!dollyCamRef.current) {
			dollyCamRef.current = new THREE.Object3D();
			dollyCamRef.current.name = 'dollyCam';
			dollyCamRef.current.userData.sceneType =
				bgConf?.sceneType || 'cubemap_scene';
			scene.add(dollyCamRef.current);
		}

		addCameraToDolly(
			cameraRef.current,
			dollyCamRef.current,
			browserName,
			scene,
		);
	};

	/**
	 * @function getZoomFromProperties
	 * @description Gets zoom level from `cameraProperties`
	 * @returns {number}
	 */
	const getZoomFromProperties = () => {
		let zoomLevel = 1;
		if (isMobile && 'mobile_zoom' in cameraProperties) {
			if (cameraProperties.mobile_zoom !== 0) {
				zoomLevel = cameraProperties.mobile_zoom;
			}
		}

		if (isDesktop && 'desktop_zoom' in cameraProperties) {
			if (cameraProperties.desktop_zoom !== 0) {
				zoomLevel = cameraProperties.desktop_zoom;
			}
		}
		return zoomLevel;
	};

	/**
	 * @function setSceneCameraZoom
	 * @description Sets zoom level for scene camera from [getZoomFromProperties]{@link getZoomFromProperties}
	 */
	const setSceneCameraZoom = () => {
		cameraRef.current.zoom = getZoomFromProperties();
	};

	/**
	 * @function initRoomCamera
	 * @description Sets up camera for scene. Uses [createOrGetCamera]{@link createOrGetCamera}
	 * to create or get camera.
	 * Sets zoom level for scene camera from [setSceneCameraZoom]{@link setSceneCameraZoom}
	 * Sets up camera controls from [setupCamera]{@link setupCamera}
	 */
	const initRoomCamera = () => {
		// Setup camera for scene
		cameraRef.current = createOrGetCamera(canvasRef, type, fov || 70);
		setSceneCameraZoom();
		// cameraRef.current.zoom = 1.75;
		setupCamera(cameraRef.current);
	};

	/**
	 * @function initRoomControls
	 * @description Sets up controls for scene. Uses [createOrGetControls]{@link createOrGetControls}
	 * to create or get controls.
	 *
	 * Uses `props.orbitControlsConfig` to set up controls by passing it to [updateFromConfig]{@link CustomControls.updateFromConfig}
	 *
	 * Triggers autoRotate for scene using [setupAutoRotate]{@link CustomCameraControls.setupAutoRotate} if user has not interacted with scene or if `{@link props.autoRotateConfig.activateOnEachSceneLoad}` is true
	 *
	 * Sets up damping factors for scene with [setDamping]{@link setDamping} using [`props.dampingFactors`]{@link props.dampingFactors}
	 *
	 */
	const initRoomControls = () => {
		controlsRef.current = createOrGetControls(
			cameraRef,
			rendererRef.current,
			orbitControlsConfig,
			type,
		);

		controlsRef.current.updateFromConfig(
			orbitControlsConfig,
			bgConf?.sceneType,
		);

		// Disable Rotate for flat scene
		const enableRotate = !(
			bgConf?.isFlatScene ||
			orbitControlsConfig?.camera_rotation === false
		);

		// ThreeController.setupPanControls(enablePan, enableRotate);
		ThreeController.setupRotation(enableRotate);
		if (
			!userInteracted ||
			(userInteracted && autoRotateConfig.activateOnEachSceneLoad)
		) {
			controlsRef.current.setupAutoRotate(autoRotateConfig);
		}
		dampingFactors && controlsRef.current.setDamping(dampingFactors);
	};

	/**
	 * @function initRoomCss2DRenderer
	 * @description Sets up css2DRenderer for scene
	 */
	const initRoomCss2DRenderer = () => {
		const canvasContainer = canvasRef.current;
		css2DRenderer.setSize(
			canvasContainer?.offsetWidth,
			canvasContainer?.offsetHeight,
		);
	};

	/**
	 * @function initRoomCss3DRenderer
	 * @description Sets up css3DRenderer for scene
	 */
	const initRoomCss3DRenderer = () => {
		const canvasContainer = canvasRef.current;
		css3DRenderer.setSize(
			canvasContainer?.offsetWidth,
			canvasContainer?.offsetHeight,
		);
	};

	/**
	 * @function initRoomAnimationLoop
	 * @description Initiates animation loop for scene
	 */
	const initRoomAnimationLoop = () => {
		animate(controlsRef.current.update);
	};

	/**
	 *
	 * @function initRoom
	 * @description Initiates room setup. Triggered on {@link sceneId} change.
	 *
	 * @fires {@link initRoomCamera}
	 * @fires {@link initRoomControls}
	 * @fires {@link initRoomCss2DRenderer}
	 * @fires {@link initRoomCss3DRenderer}
	 * @fires {@link initRoomAnimationLoop}
	 */
	const initRoom = () => {
		setShowLoadingIcon(true);
		setRenderObjects(false);

		// Initialize HotspotsContainer with proximity config
		const hotspotsContainer = HotspotsContainer.getInstance();
		if (proximityConfig) {
			hotspotsContainer.setGlobalProximityConfig(proximityConfig);
		}

		initRoomCamera();
		initRoomControls();
		initRoomCss2DRenderer();
		initRoomCss3DRenderer();
		initRoomAnimationLoop();
	};

	/**
	 * @function disposeHotspots
	 * @description Disposes hotspots from scene, called on scene change.
	 */
	const disposeHotspots = () => {
		for (let i = scene.children.length - 1; i >= 0; i--) {
			const child = scene.children[i];
			// video360Background cleanup is handled by VideoEquirectangular for performance
			if (child?.dispose && child?.name !== 'video360Background') {
				child.dispose();
			} else {
				if (child?.material?.length) {
					child.material.forEach((mesh) => {
						mesh?.map?.dispose();
						mesh?.dispose();
					});
				}
				if (child?.material?.map) {
					child.material.map?.dispose();
					child.material.map = null;
				}

				if (child?.type === 'PerspectiveCamera') {
					scene.remove(child);
				}

				// css2DRenderer.innerHtml
				const label_types = new Set(['navigation_label', 'tooltip']);
				if (label_types.has(child?.name)) {
					scene.remove(child);
				}

				if (child?._type === 'InSceneComponent') {
					scene.remove(child);
				}
			}
		}
	};

	/**
	 * @function teardownRoom
	 * @description Disposes hotspots from scene, called on scene change.
	 *
	 *  @fires {@link disposeHotspots}
	 * @fires {@link setUI}
	 * @fires {@link clearAnimation}
	 *
	 */
	const teardownRoom = () => {
		disposeHotspots();
		setUI(false); //Hide UI Modal when scene changed
		clearAnimation();
		activeHotspotRef.current = null;
		delaysRef.current = [];
		setDelays(null);
	};

	/**
	 * @description useEffect that listens for {@link sceneId} change.
	 * If {@link sceneId} changes, it will call {@link teardownRoom} and {@link initRoom}
	 *
	 */
	//New Scene INIT
	useEffect(() => {
		initRoom();
		return teardownRoom;
	}, [sceneId]);

	/**
	 * @function setTooltipsVisibility
	 * @param visible {boolean} - Sets visibility of tooltips
	 */
	const setTooltipsVisibility = (visible) => {
		sceneRef.current.children.forEach((child) => {
			if (child.name === 'tooltip' && child.visible) {
				child?.owner?.setVisibility(visible);
			}
		});
	};

	/**
	 * @function onMouseUpMarker
	 *
	 * @description Callback for onMouseUp event on marker.
	 *
	 * If navigation has been interacted with, it will set {@link interactedWithNavMarker} to true.
	 *
	 * If marker has `focusOnClick` set to true, it will rotate camera towards marker and then call {@link props.onMouseUp}. Implemented for all hotspots, and can be set from AVA.
	 *
	 * If marker has `props.data.rotateTowardsSpecificPoint` set to true, it will rotate camera towards that point and then call {@link props.onMouseUp}. Implemented for {@link InteractiveGLBComponent}
	 *
	 * Otherwise, it will call {@link props.onMouseUp}
	 *
	 * @param e
	 * @param sceneObject
	 * @param marker
	 * @param isDragEvent
	 */
	const onMouseUpMarker = (e, sceneObject, marker, isDragEvent) => {
		if (marker?.userData?.type === 'NavMarker') {
			setInteractedWithNavMarker(true);
		}
		if (marker?.userData?.focusOnClick && marker?.sceneObject) {
			rotateCameraTowardsPoint(
				marker.sceneObject.position,
				cameraRef.current,
				controlsRef.current,
			).then(() => {
				props.onMouseUp(e, sceneObject, marker, isDragEvent);
			});
		} else if (marker?.userData?.props?.data?.rotateTowardsSpecificPoint) {
			rotateCameraTowardsPoint(
				marker.userData.props.data?.rotateTowardsSpecificPoint,
				cameraRef.current,
				controlsRef.current,
			).then(() => {
				props.onMouseUp(e, sceneObject, marker, isDragEvent);
			});
		} else {
			props.onMouseUp(e, sceneObject, marker, isDragEvent);
		}
	};

	/**
	 * @function updateXrSessionFramerate
	 * @description sets the framerate for the xr session based on device
	 */
	const updateXrSessionFramerate = () => {
		const xrSession = rendererRef.current.xr.getSession();
		const targetFrameRate = browserName === 'Oculus Browser' ? 60 : 90;
		if (xrSession?.updateTargetFrameRate) {
			xrSession.updateTargetFrameRate(targetFrameRate);
		}
	};

	// initialize the VR session when it is enabled
	useEffect(() => {
		if (isXREnabled) {
			setupXR(rendererRef.current);
			initDollyforVR();
			initXRSession({
				renderer: rendererRef.current,
				sessionType: xrSessionType,
				optionalFeatures: xrOptionalFeatures,
				onXrSessionStarted: () => setIsXRSessionReady(true),
				onXRSessionEnd: handleXRSessionEnd,
			});
		} else {
			// Clean up VR state when VR is disabled to prevent stale state on re-enable
			ThreeController.cleanupVRState();

			if (dollyCamRef.current?.children.length > 0) {
				if (
					dollyCamRef.current.children.find(
						(c) => c.type === 'PerspectiveCamera',
					)
				) {
					removeCameraFromDolly(
						cameraRef.current,
						dollyCamRef.current,
						browserName,
						scene,
					);
				}
			}
		}
	}, [isXREnabled]);

	useEffect(() => {
		isXRSessionReady && initRoomForVR();
	}, [isXRSessionReady]);
	/**
	 * @description useEffect that listens for {@link sceneRef} and {@link vrControlsRef} change.
	 *
	 * If {@link sceneRef} and {@link vrControlsRef} changes and {@link isXREnabled} is true, it will call {@link threeEditorVREvents} to add VR event listeners.
	 */
	useEffect(() => {
		// Add VR event listeners
		if (isXREnabled && vrControlsLoaded) {
			const {
				addThreeEditorVREventListeners,
				removeThreeEditorVREventListeners,
			} = threeEditorVREvents(
				rendererRef.current,
				sceneRef.current,
				vrControlsRef.current,
				vrGripControlsRef.current,
				vrHandsRef.current,
				cameraRef.current,
				dollyCamRef.current,
				props.onMouseUp,
			);

			addThreeEditorVREventListeners();
			updateXrSessionFramerate();

			return () => {
				removeThreeEditorVREventListeners();
			};
		}
	}, [vrControlsLoaded]);

	useEffect(() => {
		if (isXREnabled) {
			if (dollyCamRef.current)
				dollyCamRef.current.userData.sceneType = bgConf?.sceneType;
			if (bgConf?.sceneType === '3d_model_scene') {
				rendererRef.current.xr.setReferenceSpaceType('local-floor');
			} else if (bgConf?.sceneType === 'cubemap_scene') {
				rendererRef.current.xr.setReferenceSpaceType('local');
			}
		}
	}, [bgConf]);

	/**
	 * @function clearAnimation
	 * @description Clears animation loop
	 */
	const clearAnimation = () => {
		rendererRef.current?.setAnimationLoop(null);
	};

	//windowResizer placed separately because it requires to track and call UI & setUI
	//while at same time we DO NOT WANT to remount all events each time when UI changed
	//reminder: UI changed on each on scene click
	useEffect(() => {
		const windowResizeHandler = () => {
			const canvasContainer = canvasRef.current;
			const width = canvasContainer.offsetWidth;
			const height = canvasContainer.offsetHeight;

			cameraRef.current.aspect = width / height;
			cameraRef.current.updateProjectionMatrix();
			css2DRenderer.setSize(width, height);
			css3DRenderer.setSize(width, height);

			if (UI) setUI(false); //destroy UI
		};

		window.addEventListener('resize', windowResizeHandler);
		return () => {
			window.removeEventListener('resize', windowResizeHandler);
		};
	}, [UI]);

	/**
	 * @function clearRoom
	 * @description Clears room. Disposes renderer and controls.
	 */
	const clearRoom = () => {
		// rendererRef.current = null;

		// Controls
		controlsRef.current?.dispose();
		delete window[`${type}_controller`];

		delete window[`${type}_camera`];
	};

	/**
	 * @function onAvatarLoaded
	 * @description Callback for avatar loaded event.
	 * Sets {@link showLoadingIcon} to false for 3d_model_scene.
	 * @fires {@link onBackgroundLoaded} for 3d_model_scene.
	 *
	 */
	const onAvatarLoaded = () => {
		setShowLoadingIcon(false);
		onBackgroundLoaded();
	};

	/**
	 * @function _onBackgroundReady
	 * @description Callback for background ready event.
	 * Sets {@link showLoadingIcon} to false for non 3d_model_scene
	 * @fires {@link onBackgroundReady}
	 * @private
	 */
	const _onBackgroundReady = () => {
		/**
		 * stopgap solution to prevent loading icon from showing when there are delays added from other components
		 */
		setTimeout(() => {
			if (delaysRef.current.length === 0) {
				setShowLoadingIcon(false);
			}
		}, [50]);
		// if (bgConf?.sceneType !== '3d_model_scene') setShowLoadingIcon(false);
		setRenderObjects(true);
		onBackgroundReady(true);
	};

	/**
	 * @function _onBackgroundError
	 * @description Callback for background error event.
	 * Sets {@link showLoadingIcon} to false
	 * @fires {@link onSceneError}
	 * @param e {Event} - Error event
	 * @private
	 */
	const _onBackgroundError = (e) => {
		setShowLoadingIcon(false);
		onSceneError(e);
	};

	const _renderAvatars = () => {
		return (
			<Suspense fallback={<></>}>
				<AvatarLoader
					scene={scene}
					sceneId={sceneId}
					camera={cameraRef.current}
					controller={controlsRef.current}
					renderrer={rendererRef.current}
					avatarConfig={selectedAvatar}
					envMapIntensity={bgConf.envMapIntensity}
					onInteractedWithHotspot={onMouseUpMarker}
					avatarConfiguration={avatarConfiguration}
					interactedWithNavMarker={interactedWithNavMarker}
					onAvatarLoaded={onAvatarLoaded}
					sceneModelConfig={bgConf?.scene_model}
					RT3DControlsRef={RT3DControlsRef}
					RT3DControlsReady={RT3DControlsReady}
					orbitControlsConfig={orbitControlsConfig}
				/>
			</Suspense>
		);
	};

	const [delays, setDelays] = useState(0);
	const delaysRef = useRef([]);

	/**
	 * @function processDelays
	 *
	 * @description Used to delay the removal of the loading icon till a certain component has finished loading.
	 * Whenever delays changes, this useEffect gets triggered and waits for all the promises in delaysRef to resolve and then removes the loading icon..
	 *
	 */
	useEffect(() => {
		if (delays > 0) {
			setShowLoadingIcon(true);
			Promise.all(delaysRef.current).then(() => {
				setShowLoadingIcon(false);
				allHotspotsLoaded();
			});
		}
	}, [delays]);

	/**
	 * @function addToDelays
	 * @description Used to delay the removal of the loading icon till a certain component has finished loading.
	 * Maintains a list of promises that resolve when a component has finished loading.
	 *
	 * - Adds a promise to the list of delays
	 * - Sets delays state to length of delays list which triggers a useEffect to remove loading icon when all delays have resolved.
	 *
	 * This function is passed to all the children of the Scene and can be consumed from props.
	 *
	 * @example
	 * const MyComponentThatRequiresDelay = (props) => {
	 * 	const { addToDelays } = props;
	 *
	 * 	const myLoadFunction = (url) => {
	 * 	    const promiseThatResolves = new Promise((resolve, reject) => {
	 * 	    	// Load the component
	 * 	    	// ...
	 * 	    	resolve();
	 * 	    }
	 * 	    addToDelays(promiseThatResolves);
	 *  }
	 * 	return <>{component returns}</>
	 * 	}
	 *
	 * @param e {Promise} - Promise that resolves when component has finished loading
	 */
	const addToDelays = (e) => {
		delaysRef.current.push(e);
		setDelays(delaysRef.current.length);
	};

	const renderDebugUI = () => {
		// document.body.appendChild(statsRef.current.dom);
		const { statsJS } = debuggerConfig;
		if (statsJS?.enabled) {
			statsRef.current.init(rendererRef.current);

			const DebugUI = React.lazy(() => import('@3jss/utils/DebugUI'));

			return (
				<Suspense fallback={<></>}>
					<DebugUI
						rendererRef={rendererRef}
						scene={sceneRef.current}
						statsRef={statsRef}
						debuggerConfig={debuggerConfig}
					/>
				</Suspense>
			);
		}
	};

	console.log('SCENE===', { showLoadingIcon, rendererRef });
	return (
		<>
			<div
				id={props.id ? props.id : 'canvas-wrapper'}
				className={props.id ? props.id : 'canvas-wrapper'}
				ref={canvasRef}
				onDragOver={(e) => e.preventDefault()}
			>
				{threeReady && (
					<>
						<Renderer
							rendererRef={rendererRef}
							sceneId={sceneId}
							canvasRef={canvasRef}
							renderer_config={props?.renderer_config}
							clearColor={
								type === 'zoom' && enableZoomControls
									? '0x000000'
									: 'black'
							}
						>
							{debuggerConfig && renderDebugUI()}

							{!showEntranceVideo ? (
								<>
									<MouseListener
										isScrollDisabled={
											controlMode === CONTROL_MODES.SCROLL
										}
										onMouseUp={onMouseUpMarker}
										sceneRef={sceneRef}
										cameraRef={cameraRef}
										rendererRef={rendererRef}
										orbitControlsConfig={
											orbitControlsConfig
										}
									/>

									<KeyboardListener
										sceneId={sceneId}
										controlsRef={controlsRef}
										isFlatScene={bgConf?.isFlatScene}
									/>

									<Background
										renderer={rendererRef.current}
										bgConf={bgConf}
										scene={scene}
										transition={transition}
										camera={cameraRef.current}
										resetBGBeforeImageLoaded={
											props?.resetBGBeforeImageLoaded
										}
										enablePan={enablePan && isMobile}
										type={type}
										controller={controlsRef.current}
										onBackgroundReady={_onBackgroundReady}
										onBackgroundLoaded={onBackgroundLoaded}
										onBackgroundError={_onBackgroundError}
										enableZoomControls={enableZoomControls}
										sceneId={sceneId}
										avatarUrl={selectedAvatar?.glbUrl}
										isXREnabled={isXREnabled}
									/>
									{bgConf?.sceneType === '3d_model_scene' && (
										<Suspense fallback={<></>}>
											<SceneModelObject
												setCurrentCamera={
													setCurrentCamera
												}
												scene={scene}
												{...bgConf}
												onBackgroundReady={
													_onBackgroundReady
												}
												controller={controlsRef.current}
												camera={cameraRef.current}
												renderer={rendererRef.current}
												RT3DControlsRef={
													RT3DControlsRef
												}
												orbitControlsConfig={{
													...orbitControlsConfig,
													...sceneOrbitControlsConfig,
												}}
												rotateCameraTowardsMovementDirection={
													rotateCameraTowardsMovementDirection
												}
												walkVelocity={
													avatarConfiguration.walk_velocity
												}
												controlMode={controlMode}
												hasAvatar={!!selectedAvatar}
												isXREnabled={isXREnabled}
												setRT3DControlsReady={
													setRT3DControlsReady
												}
											/>
										</Suspense>
									)}
								</>
							) : (
								<>
									{/* Entrance Video */}
									<VideoEquirectangular
										scene={scene}
										onBackgroundReady={onBackgroundReady}
										onBackgroundLoaded={onBackgroundLoaded}
										backgroundUrl={entranceVideoUrl}
										videoBackgroundOptions={{
											loop: false,
											muted: true,
											autoplay: true,
										}}
										onBackgroundError={_onBackgroundError}
										onVideoEnd={onEntranceVideoEnd}
									/>
								</>
							)}
						</Renderer>

						{!showEntranceVideo && (
							<>
								{renderObjects &&
									selectedAvatar &&
									bgConf?.sceneType === '3d_model_scene' &&
									controlMode ===
										CONTROL_MODES.AVATAR_NAVIGATION &&
									_renderAvatars()}

								{renderObjects &&
									React.Children.map(children, (child) => {
										if (child) {
											return React.cloneElement(child, {
												...child.props, //spread! props here
												scene: sceneRef.current, //keep this version
												sceneRef,
												css3DScene:
													css3DSceneRef.current,
												camera: cameraRef.current,
												canvas: canvasRef.current,
												controls: controlsRef.current,
												renderer: rendererRef.current,
												controlMode: controlMode,
												bgConf,
												addToDelays,
												enableDepthTest:
													bgConf?.sceneType ===
													'3d_model_scene',
												isXREnabled,
											});
										} else return false;
									})}

								{showLoadingIcon && (
									<LoadingIcon
										src={loadingIconSrc}
										primaryColor={primaryColor}
										secondaryColor={secondaryColor}
									/>
								)}

								<div
									id="canvasUI"
									className={`canvasUI ${UI ? 'active' : ''}`}
									style={UI?.style}
								>
									{UI && (
										<UI.Component
											{...UI?.props}
											sceneRef={sceneRef}
										/>
									)}
								</div>
							</>
						)}
					</>
				)}
			</div>
		</>
	);
};

Scene.defaultProps = {
	onMouseUp: () => {},
};

/**
 *
 * @typedef {Object} props
 * @description PropTypes for Scene
 * @property {string} sceneId - Scene ID
 * @property {Object} bgConf - Background Configuration
 * @property {string} sceneType - Type of Scene. One of `3d_model_scene`, `3d_scene`, `cubemap_scene`, `flat_scene`, `video_equirectangular`
 * @property {Object} renderer_config - Renderer Configuration
 * @property {Number} renderer_config.tone_mapping_exposure - Tone Mapping Exposure
 * @property {string} renderer_config.tone_mapping - Tone Mapping. One of `no`, `linear`, `reinhard`, `cineon`, `aces`
 * @property {Object} transition - Transition Configuration
 * @property {boolean} transition.enable - Enable Transition
 * @property {string} transition.type - Type of Transition. One of `zoom`, `fade`, `zoom & fade`
 * @property {Object} transition.props - Transition Properties
 * @property {boolean} enablePan - Enable Pan
 * @property {Object} orbitControlsConfig - Orbit Controls Configuration
 * @property {number} orbitControlsConfig.starting_cam_vertical_angle - Starting Camera Vertical Angle
 * @property {number} orbitControlsConfig.min_vertical_angle - Minimum Vertical Angle
 * @property {number} orbitControlsConfig.max_vertical_angle - Maximum Vertical Angle
 * @property {number} orbitControlsConfig.starting_cam_horizontal_angle - Starting Camera Horizontal Angle
 * @property {number} orbitControlsConfig.min_horizontal_angle - Minimum Horizontal Angle
 * @property {number} orbitControlsConfig.max_horizontal_angle - Maximum Horizontal Angle
 * @property {boolean} orbitControlsConfig.camera_rotation - Enable Camera Rotation
 * @property {Object} orbitControlsConfig.zoom - Zoom Configuration
 * @property {boolean} orbitControlsConfig.zoom.desktop - Enable Zoom on Desktop
 * @property {boolean} orbitControlsConfig.zoom.mobile - Enable Zoom on Mobile
 * @property {Object} dampingFactors - Damping Factors
 * @property {boolean} dampingFactors.enable - Enable Damping Factors
 * @property {number} dampingFactors.factor - Damping Factor
 * @property {Object} cameraProperties - Camera Properties
 * @property {number} cameraProperties.desktop_zoom - Desktop Zoom
 * @property {number} cameraProperties.mobile_zoom - Mobile Zoom
 * @property {boolean} enableZoomControls - Enable Zoom Controls, only for zoom scenes
 * @property {Object} selectedAvatar - Selected Avatar Configuration
 * @property {string} selectedAvatar._id - Avatar ID
 * @property {string} selectedAvatar.glbUrl - Avatar GLB URL
 * @property {string} selectedAvatar.image - Avatar Image
 * @property {string} selectedAvatar.name - Avatar Name
 * @property {number} selectedAvatar.scale - Avatar Scale
 * @property {Array} selectedAvatar.animations - Avatar Animations
 * @property {Array} selectedAvatar.customizations - Avatar Customizations
 * @property {Object} avatarConfiguration - Avatar World Configuration
 * @property {Object} avatarConfiguration.orbit_controls - Orbit Controls Configuration
 * @property {number} avatarConfiguration.orbit_controls.min_distance - Minimum Distance
 * @property {number} avatarConfiguration.orbit_controls.max_distance - Maximum Distance
 * @property {boolean} avatarConfiguration.avatar_configuration - Avatar Configuration
 * @property {number} avatarConfiguration.walk_velocity - Walk Velocity
 * @property {boolean} allowHotspotsToMove - Enable Hotspots to move
 * @property {string} allowEventsForMarkerTypeOnly - Allow Events for Marker Type Only
 * @property {string} loadingIconSrc - Loading Icon Source URL
 * @property {string} primaryColor - Primary Color
 * @property {string} secondaryColor - Secondary Color
 * @property {Function} onBackgroundLoaded - Callback for Background Loaded
 * @property {Function} onBackgroundReady - Callback for Background Ready
 * @property {Function} onSceneError - Callback for Scene Error
 * @property {Object} autoRotateConfig - Auto Rotate Configuration
 * @property {boolean} autoRotateConfig.enabled - Enable Auto Rotate
 * @property {boolean} autoRotateConfig.activateOnEachSceneLoad - Activate on each scene load
 * @property {boolean} autoRotateConfig.cancelAutoRotateOnInteraction - Cancel Auto Rotate on Interaction
 * @property {number} autoRotateConfig.autoRotateSpeed - Auto Rotate Speed
 * @property {number} autoRotateConfig.autoRotateDelay - Auto Rotate Delay
 * @property {string} autoRotateConfig.autoRotateDirection - Auto Rotate Direction
 * @property {Object} tooltipComponents - Tooltip Components
 * @property {Object} inSceneComponents - In Scene Components
 * @property {Object} tooltipComponents.Component - Tooltip Component
 * @property {Object} tooltipComponents.props - Tooltip Component Props
 * @property {Object} inSceneComponents.Component - In Scene Component
 * @property {Object} inSceneComponents.props - In Scene Component Props
 * @property {function} onMouseUp - Callback for Mouse Up
 * @property {function} onDrop - Callback for Drop
 * @property {boolean} resetBGBeforeImageLoaded - Reset Background Before Image Loaded
 * @property {Object} debuggerConfig - Debugger Configuration
 * @property {string} id - ID
 * @property {node} children - Children
 * @property {number} fov - FOV (Field of View) for camera.
 * @property {string} type - Type
 * @property {bool} isXREnabled - Enable XR session
 * @property {string} xrSessionType - immersive-vr or immersive-ar session
 * @property {array} xrOptionalFeatures - optional XR session features
 * @property {object} xrEnvMapConfig - Environment map configuration for XR
 * @property {Object} proximityConfig - Global proximity configuration
 * @property {boolean} proximityConfig.enabled - Enable global proximity
 * @property {number} proximityConfig.threshold - Global proximity threshold
 *
 */
Scene.propTypes = {
	sceneId: PropTypes.string.isRequired,
	bgConf: PropTypes.object.isRequired,
	sceneType: PropTypes.oneOf([
		'3d_model_scene',
		'3d_scene',
		'cubemap_scene',
		'flat_scene',
		'video_equirectangular',
	]).isRequired,
	renderer_config: PropTypes.shape({
		tone_mapping_exposure: PropTypes.number,
		tone_mapping: PropTypes.oneOf([
			'no',
			'linear',
			'reinhard',
			'cineon',
			'aces',
		]),
	}),
	transition: PropTypes.shape({
		enable: PropTypes.bool,
		type: PropTypes.oneOf(['zoom', 'fade', 'zoom & fade']),
		props: PropTypes.object,
	}),
	enablePan: PropTypes.bool,
	orbitControlsConfig: PropTypes.shape({
		starting_cam_vertical_angle: PropTypes.number,
		min_vertical_angle: PropTypes.number,
		max_vertical_angle: PropTypes.number,
		starting_cam_horizontal_angle: PropTypes.number,
		min_horizontal_angle: PropTypes.number,
		max_horizontal_angle: PropTypes.number,
		camera_rotation: PropTypes.bool,
		zoom: PropTypes.shape({
			desktop: PropTypes.bool,
			mobile: PropTypes.bool,
		}),
	}),
	cameraProperties: PropTypes.shape({
		desktop_zoom: PropTypes.number,
		mobile_zoom: PropTypes.number,
	}),
	enableZoomControls: PropTypes.bool,
	selectedAvatar: PropTypes.shape({
		_id: PropTypes.string,
		glbUrl: PropTypes.string,
		image: PropTypes.string,
		name: PropTypes.string,
		scale: PropTypes.number,
		animations: PropTypes.array,
		customizations: PropTypes.array,
		initialRotation: PropTypes.number,
	}),
	avatarConfiguration: PropTypes.shape({
		orbit_controls: PropTypes.shape({
			min_distance: PropTypes.number,
			max_distance: PropTypes.number,
		}),
		avatar_configuration: PropTypes.bool,
		walk_velocity: PropTypes.number,
	}),
	allowHotspotsToMove: PropTypes.bool,
	allowEventsForMarkerTypeOnly: PropTypes.string,
	loadingIconSrc: PropTypes.string,
	primaryColor: PropTypes.string,
	secondaryColor: PropTypes.string,
	onBackgroundLoaded: PropTypes.func,
	onBackgroundReady: PropTypes.func,
	onSceneError: PropTypes.func,
	autoRotateConfig: PropTypes.shape({
		enabled: PropTypes.bool,
		activateOnEachSceneLoad: PropTypes.bool,
		cancelAutoRotateOnInteraction: PropTypes.bool,
		autoRotateSpeed: PropTypes.number,
		autoRotateDelay: PropTypes.number,
		autoRotateDirection: PropTypes.oneOf(['left', 'right']),
	}),
	tooltipComponents: PropTypes.object,
	inSceneComponents: PropTypes.object,
	dampingFactors: PropTypes.shape({
		enable: PropTypes.bool,
		factor: PropTypes.number,
	}),
	onMouseUp: PropTypes.func,
	onDrop: PropTypes.func,
	resetBGBeforeImageLoaded: PropTypes.bool,
	debuggerConfig: PropTypes.object,
	id: PropTypes.string,
	children: PropTypes.node,
	fov: PropTypes.number,
	type: PropTypes.string,
	isXREnabled: PropTypes.bool,
	xrSessionType: PropTypes.oneOf(['immersive-vr', 'immersive-ar']),
	xrOptionalFeatures: PropTypes.array,
	xrEnvMapConfig: PropTypes.shape({
		url: PropTypes.string,
		intensity: PropTypes.number,
	}),
	entranceVideoUrl: PropTypes.string,
	showEntranceVideo: PropTypes.bool,
	onEntranceVideoEnd: PropTypes.func,
	proximityConfig: PropTypes.shape({
		enabled: PropTypes.bool,
		threshold: PropTypes.number,
	}),
};

export default Scene;
